<template>
  <ValidationObserver
    ref="observer"
    v-slot="{ handleSubmit }"
  >
    <b-modal
      v-model="modal.show"
      :title="modalTitle"
      body-class="batch-file-modal"
      ok-title="Submit"
      size="xl"
      scrollable
      :ok-disabled="loading"
      @ok.prevent="handleSubmit(onSubmit, isEntityFieldValid = !!formFromStore.entityGuid.value)"
      @show="onShowModal"
    >
      <content-spinner :show="loading" />
      <div
        v-if="!loading"
        class="batch-file__form"
      >
        <general-fields
          :batch-file="batchFile"
          :is-entity-guid-valid="isEntityFieldValid"
        />
        <div class="textarea__wrapper">
          <inner-configurations v-if="isInnerConfigurationBlockVisible" />
        </div>
        <flat-file
          v-if="formFromStore.batchFileType"
          :batch-file="batchFile"
          :is-balk-flat-file="isBulkFlatFile"
        />
        <div
          v-if="formFromStore.batchFileType === 'excel'"
          class="excel-sheets"
        >
          <b-button
            id="add-sheet-configurations-item"
            class="add-new-sheet-config-btn"
            size="sm"
            @click="onAddNewSheetConfigClick"
          >
            <font-awesome-icon :icon="['fas', 'plus']" />
            Add new sheet config
          </b-button>
          <complex-table-view
            v-if="formFromStore.sheetConfigs"
            :is-file-manipulation-controller="true"
            :small="true"
            :fields="sheetConfigFields"
            :items="formFromStore.sheetConfigs"
            :total-rows="formFromStore.sheetConfigs.length"
            :modal-name="`sheet-configs-modal`"
            :action-buttons="actionButtons"
            @on-edit="editSheetConfig"
            @on-delete="onSheetConfigDeleteClick"
          />
        </div>
      </div>
      <add-edit-sheet-config-modal
        v-if="isAddEditSheetConfigModalOpen"
        @submit="onSubmitSheetConfigModal"
      />
    </b-modal>
  </ValidationObserver>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import InputWidget from '../../../../core/components/complex-table/components/InputWidget';
import ContentSpinner from '../../../../core/components/ContentSpinner';
import ComplexTableView from '../../../../core/components/complex-table/components/ComplexTableView';
import AddEditSheetConfigModal from './AddEditSheetConfigModal';
import {fileManipulationMixin} from '@/modules/FileManipulationController/file-manipulation.mixin';
import GeneralFields
  from '@/modules/FileManipulationController/BatchList/components/AddEditBatchModalParts/GeneralFields.vue';
import FlatFile from '@/modules/FileManipulationController/BatchList/components/AddEditBatchModalParts/FlatFile.vue';
import InnerConfigurations
  from '@/modules/FileManipulationController/BatchList/components/AddEditBatchModalParts/InnerConfigurations.vue';
import NoContent from '@/core/components/NoContent.vue';
import {removeItemFromExcelSheet, updateBatchFilesList} from '@/store/modules/file-manipulation-controller/actions';
import {uuid} from 'vue-uuid';

const FILE_TYPES = {
  EXCEL: 'excel',
  BULK: 'bulkFlatFile',
  FLAT: 'flatFile',
};

const UPLOAD_OPERATION_TYPES_ENUM = {
  upsert: 0,
  insert: 1,
  update: 2,
  partialUpdate: 3,
};

const UPLOAD_RECORD_HANDLING_TYPES_ENUM = {
  failAll: 0,
  rejectRow: 1,
};

const UPLOAD_TYPES_ENUM = {
  allRecords: 0,
  insertsOnly: 1,
  updatesOnly: 2,
};
const {
  mapState: mapStateDataManagement,
} = createNamespacedHelpers('dataManagement');
const {
  mapState: fileManipulationMapState,
  mapActions: fileManipulationMapActions,
} = createNamespacedHelpers('fileManipulationController');
const {
  mapState: sidebarMapState,
} = createNamespacedHelpers('sidebar');

export default {
  name: 'AddEditBatchFileModal',
  mixins: [fileManipulationMixin],
  components: {
    NoContent,
    InnerConfigurations,
    FlatFile,
    GeneralFields,
    AddEditSheetConfigModal,
    ContentSpinner,
    ComplexTableView,
    InputWidget,
  },
  data() {
    return {
      isEntityFieldValid: true,
      defaultFieldsCopy: null,
      isBulkFlatFile: false,
      sheetConfigFields: [
        {
          key: 'sheetName',
          name: 'sheetName',
          label: 'Name',
        },
      ],
      actionButtons: {
        edit: true,
        delete: true,
      },
      loading: false,
    };
  },
  watch: {
    'formFromStore.entityGuid': {
      handler(val) {
        if(val && val.value && !this.isEntityFieldValid) {
          this.isEntityFieldValid = true;
        }
      },
      deep: true,
    },
  },
  computed: {
    ...fileManipulationMapState({
      isAddEditSheetConfigModalOpen: state => state.addEditSheetConfigModal.show,
      modal: state => state.addEditBatchFileModal,
      batch: state => state.addEditBatchFileModal.data.batch || {},
      batchFile: state => state.addEditBatchFileModal.data.batchFile || {},
      formFromStore: state => state.formFromStore,
    }),
    ...sidebarMapState({
      tablesList: state => state.tablesList,
    }),
    ...mapStateDataManagement({
      entityList: state => state.tables.data,
    }),

    isInnerConfigurationBlockVisible() {
      return ('innerConfig' in this.formFromStore || 'bulkInnerConfig' in this.formFromStore);
    },
    fields() {
      const fields = {};

      Object.keys(this.defaultFieldsTest).forEach(fieldName => {
        if (
          this.form.batchFileType === FILE_TYPES.EXCEL
            && (fieldName === 'delimiter'
                || fieldName === 'useColumnPhysicalNames'
                || fieldName === 'batchSize'
            )
        ) {
          return false;
        }
        if (this.form.batchFileType === FILE_TYPES.FLAT
            && (fieldName === 'batchSize')
        ) {
          return false;
        }

        if (!this.isEditMode && fieldName === FILE_TYPES.BULK) {
          return false;
        }

        fields[fieldName] = this.defaultFieldsTest[fieldName];
      });

      return fields;
    },
    modalTitle() {
      return this.isEditMode ? 'Edit Batch File' : 'Add New Batch File';
    },
    isEditMode() {
      return !!Object.keys(this.batchFile).length;
    },
    generateMainConfiguration() {
      return {
        batchFileName: this.formFromStore.batchFileName,
        batchFileType: this.formFromStore.batchFileType,
        batchGuid: this.batchFile.batchGuid || this.batch.batchGuid,
        dependencyGroup: this.formFromStore.dependencyGroup,
        batchFileGuid: this.batchFile.batchFileGuid || uuid.v4(),
        enitiyGuid: this.formFromStore.entityGuid.value,
      };
    },
  },
  methods: {
    ...fileManipulationMapActions([
      'upsertBatchFile',
      'showAddEditSheetConfigModal',
      'hideAddEditSheetConfigModal',
      'hideAddEditBatchFileModal',
      'updateFormInStore',
      'resetFormInStore',
      'removeItemFromExcelSheet',
      'getBatchList',
      'updateBatchFilesList',
    ]),
    getCurrentConfigForExcelType() {
      if (this.formFromStore.batchFileType !== 'excel') return;
      if (this.formFromStore.useBulkUpload) {
        return {
          addMissingColumns: this.formFromStore.bulkInnerConfig.addMissingColumns || false,
          columnNamesAreDisplayNames: this.formFromStore.bulkInnerConfig.columnNamesAreDisplayNames,
          expectingAllColumns: this.formFromStore.bulkInnerConfig.expectingAllColumns,
          implicitDelete: this.formFromStore.bulkInnerConfig.implicitDelete,
          uploadType: UPLOAD_TYPES_ENUM[this.formFromStore.bulkInnerConfig.uploadType],
          partitions: this.formFromStore.bulkInnerConfig.partitions || {},
        };
      } else {
        return {
          addEmptyColumns:this.formFromStore.innerConfig.addEmptyColumns || [],
          autoConfirmRecordsIfPreviouslyConfirmed: this.formFromStore.innerConfig.autoConfirmRecordsIfPreviouslyConfirmed,
          disablePostLoadWorkflow: this.formFromStore.innerConfig.disablePostLoadWorkflow,
          forceUpdate: this.formFromStore.innerConfig.forceUpdate,
          forceUpdateOfFailedValidationRecords: this.formFromStore.innerConfig.forceUpdateOfFailedValidationRecords,
          ignorePostedStatusUserConstraints: this.formFromStore.innerConfig.ignorePostedStatusUserConstraints,
          retrieveOnlyFailedRecords: this.formFromStore.innerConfig.retrieveOnlyFailedRecords,
          uploadAsAtDate: this.formFromStore.innerConfig.uploadAsAtDate,
          validationOnly: this.formFromStore.innerConfig.validationOnly,
          uploadOperationType: UPLOAD_OPERATION_TYPES_ENUM[this.formFromStore.innerConfig.uploadOperationType],
          uploadErrorHandling: UPLOAD_RECORD_HANDLING_TYPES_ENUM[this.formFromStore.innerConfig.uploadErrorHandling],
          lookups: this.formFromStore.innerConfig.lookups || [],
          calculatedColumns: this.formFromStore.innerConfig.calculatedColumns || [],
          inheritValueFromExistingRecord: this.formFromStore.innerConfig.inheritValueFromExistingRecord || [],
          columnsMappings: this.formFromStore.innerConfig.columnsMappings || [],
        };
      }
    },
    createRequestBody() {
      if (this.formFromStore.batchFileType === 'bulkFlatFile') {
        return {
          ...this.generateMainConfiguration,
          batchFileConfigJSON: JSON.stringify({
            batchSize: this.formFromStore.batchSize,
            delimiter: this.formFromStore.delimiter,
            fileNameColumn: this.formFromStore.fileNameColumn,
            fileNamePattern: this.formFromStore.fileNamePattern,
            ignoreQuotes: this.formFromStore.ignoreQuotes,
            treatEmptyValuesAsNull: this.formFromStore.treatEmptyValuesAsNull,
            useColumnPhysicalNames: this.formFromStore.useColumnPhysicalNames,
            hasHeaderRecord: this.formFromStore.hasHeaderRecord,
            uploadRecordConfig: {
              addMissingColumns: this.formFromStore.innerConfig.addMissingColumns || false,
              columnNamesAreDisplayNames: this.formFromStore.innerConfig.columnNamesAreDisplayNames,
              expectingAllColumns: this.formFromStore.innerConfig.expectingAllColumns,
              implicitDelete: this.formFromStore.innerConfig.implicitDelete,
              delimiter: this.formFromStore.delimiter,
              uploadType: UPLOAD_TYPES_ENUM[this.formFromStore.innerConfig.uploadType],
              partitions: this.formFromStore.innerConfig.partitions || {},
            },
          }),
        };
      } else if (this.formFromStore.batchFileType === 'flatFile') {
        return {
          ...this.generateMainConfiguration,
          batchFileConfigJSON: JSON.stringify({
            delimiter: this.formFromStore.delimiter,
            fileNameColumn: this.formFromStore.fileNameColumn,
            fileNamePattern: this.formFromStore.fileNamePattern,
            ignoreQuotes: this.formFromStore.ignoreQuotes,
            treatEmptyValuesAsNull: this.formFromStore.treatEmptyValuesAsNull,
            useColumnPhysicalNames: this.formFromStore.useColumnPhysicalNames,
            hasHeaderRecord: this.formFromStore.hasHeaderRecord,
            transforms: this.formFromStore.transforms || [],
            valuesToTreatAsNull: this.formFromStore.valuesToTreatAsNull || [],
            uploadRecordConfig: {
              addEmptyColumns: this.formFromStore.innerConfig.addEmptyColumns || [],
              autoConfirmRecordsIfPreviouslyConfirmed: this.formFromStore.innerConfig.autoConfirmRecordsIfPreviouslyConfirmed,
              disablePostLoadWorkflow: this.formFromStore.innerConfig.disablePostLoadWorkflow,
              forceUpdate: this.formFromStore.innerConfig.forceUpdate,
              forceUpdateOfFailedValidationRecords: this.formFromStore.innerConfig.forceUpdateOfFailedValidationRecords,
              ignorePostedStatusUserConstraints: this.formFromStore.innerConfig.ignorePostedStatusUserConstraints,
              retrieveOnlyFailedRecords: this.formFromStore.innerConfig.retrieveOnlyFailedRecords,
              uploadAsAtDate: this.formFromStore.innerConfig.uploadAsAtDate,
              validationOnly: this.formFromStore.innerConfig.validationOnly,
              uploadOperationType: UPLOAD_OPERATION_TYPES_ENUM[this.formFromStore.innerConfig.uploadOperationType],
              uploadErrorHandling: UPLOAD_RECORD_HANDLING_TYPES_ENUM[this.formFromStore.innerConfig.uploadErrorHandling],
              calculatedColumns: this.formFromStore.innerConfig.calculatedColumns || [],
              lookups: this.formFromStore.innerConfig.lookups || [],
              inheritValueFromExistingRecord: this.formFromStore.innerConfig.inheritValueFromExistingRecord || [],
              columnsMappings: this.formFromStore.innerConfig.columnsMappings || [],
              validationRulesToSkip: this.formFromStore.innerConfig.validationRulesToSkip || [],
            },
          }),
        };
      } else if (this.formFromStore.batchFileType === 'excel') {
        const batchConfig = {
          fileNamePattern: this.formFromStore.fileNamePattern,
          batchSize: this.formFromStore.batchSize,
          useBulkUpload: this.formFromStore.useBulkUpload,
          UploadBulkRecordConfig: this.formFromStore.useBulkUpload
            ? this.getCurrentConfigForExcelType()
            : null,
          config: {sheetConfigs: this.formFromStore.sheetConfigs.map(el => {
            delete el.localGuid;
            return el;
          })},
          UploadRecordConfig: !this.formFromStore.useBulkUpload
            ? this.getCurrentConfigForExcelType()
            : null,
        };

        return {
          ...this.generateMainConfiguration,
          batchFileConfigJSON: JSON.stringify(batchConfig),
        };
      }
    },
    async onSubmit() {
      if(!this.formFromStore.entityGuid.value) {
        return;
      }

      if ( !this.$refs.observer.validate() ) {
        return;
      }
      this.loading = true;
      const params = this.createRequestBody();
      const {success, body} = await this.upsertBatchFile({params});

      if (success) {
        this.updateBatchFilesList({isEditMode: this.isEditMode, item: body});
        this.$toasted.success(`Batch file ${this.isEditMode ? 'updated' : 'created'} successfully`, {
          duration: 5000,
        });
        this.$emit('on-update');

        this.loading = false;

        this.hideAddEditBatchFileModal();
      } else {
        this.$toasted.error(body.ErrorMessage, {
          duration: 5000,
        });
        this.loading = false;
      }
    },
    onShowModal() {
      this.collectAddEditBatchFileModalFormInEditMode(this.isBulkFlatFile);
    },
    onAddNewSheetConfigClick() {
      this.showAddEditSheetConfigModal();
    },
    onSubmitSheetConfigModal() {
      this.hideAddEditSheetConfigModal();
    },
    editSheetConfig(item, field, index) {
      this.showAddEditSheetConfigModal({
        batch: this.batch,
        currentEntry: {
          ...item,
          entityGuid: {
            value: item.entityGuid,
            text: this.tablesList[item.entityGuid].label,
          },
        },
      });
    },
    onSheetConfigDeleteClick(index) {
      //todo: add uniq ids during the creation sheet items
      this.removeItemFromExcelSheet(index);
    },
  },
  mounted() {
    this.isBulkFlatFile = this.batchFile.batchFileType === FILE_TYPES.BULK;
    this.defaultFieldsCopy = this.defaultFieldsTest && JSON.parse(JSON.stringify(this.defaultFieldsTest));

    this.defaultFieldsCopy.entityGuid.data.options = Object.values(this.tablesList).map(e => ({
      value: e.entityGuid,
      text: e.label,
    }));
  },
  beforeDestroy() {
    this.resetFormInStore();
  },
};
</script>

<style lang="scss">
.batch-file-modal.modal-body {
  padding: 7px 15px;
}

.batch-file__form {
  display: grid;
  grid-template-rows: minmax(min-content, 1fr);
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;

  .excel-sheets {
    margin-block: 20px;
    grid-row: 3/4;
    grid-column: 1/3;

    .card {
      height: fit-content !important;
    }

    .add-new-sheet-config-btn {
      margin-bottom: 5px;
      display: block;
      width: fit-content;
      margin-left: auto;
    }
  }

  .textarea__wrapper {
    background-color: #eee;
    padding: 10px;
    border-radius: 6px;
    grid-row: 1/3;
    grid-column: 2/3;

    .div {
      height: 100%;
    }
  }
}

.multiselect__wrapper {
  margin-bottom: 10px;

  .multiselect__tags,
  .multiselect__select {
    min-height: 35px;
  }
}

.typo__label {
  sup {
    font-size: 0.7rem;
    position: relative;
    top: -5px;
    color: red;
  }
}
</style>